// src/Components/Forms/Financement/Salam/Validation/ValidationSalam.tsx
import { useState, useEffect } from 'react';
import { Tabs, Space } from 'antd';
import Table from '../../../../TableResizable';
import type { TabsProps } from 'antd';
import { Button, Form, Spin } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import axios from 'axios';
import Tab1 from './Tab1';
import Tab2 from './Tab2';
import Tab3 from './Tab3';
import Tab4 from './Tab4';
import Utilisateur from '../../../../../Models/Utilisateur';
import { useAuthHeader, useAuthUser } from '../../../../../Services/Auth/sessionAuth';
import Data from '../../../../../Data/Data.json';
import SweetAlert from 'sweetalert2';
import { Crypter } from '../../../../../Functions/Security/Crypter';
import { Box, Typography } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import formatNumber from '../../../../../Functions/Traitement/FormatNumber';
import ReformatDate from '../../../../../Functions/Traitement/ReformatDate';
import ParamsPicklist from '../../../../../Types/ParamsPicklist';
import { DemandeSalamDto } from '../../../../../Models/DemandeSalam';
import DataTypeTable from '../../../../../Types/DataTypeTable';

const Validation = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
  const authHeader = useAuthHeader();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [, forceUpdate] = useState({});
  const [pageLoad, setPageLoad] = useState(true);
  const [data, setData] = useState<DemandeSalamDto[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [garantiesTableau, setGarantiesTableau] = useState<DataTypeTable[]>([]);
  const [documentsAttaches, setDocumentsAttaches] = useState<any[]>([]);
  const [modifActve, setModifActve] = useState(true);
  const [annulActive, setAnnulActive] = useState(true);

  // ===== ÉTATS POUR LES FRAIS =====
  const [fraisData, setFraisData] = useState<any[]>([]);
  const [fraisOptions, setFraisOptions] = useState<{ value: string; label: string }[]>([]);
  const [fraisSelectionnes, setFraisSelectionnes] = useState<string[]>([]);

  // Observateurs pour les frais
  const CodeSalam = Form.useWatch('CodeSalam', form);
  const MontantDemande = Form.useWatch('MontantDemande', form) || 0;

  // ===== CHARGEMENT DES PARAMÈTRES SALAM (FRAIS) =====
  const loadFraisParams = (codeSalam: string) => {
    if (!codeSalam) {
      setFraisData([]);
      setFraisOptions([]);
      setFraisSelectionnes([]);
      form.setFieldValue('FraisService', 0);
      form.setFieldValue('CodeFrais', []);
      return;
    }

    const config = {
      method: 'get',
      url: Data.ApiUrl.ReadParamsSalam,
      headers: {
        'CypherCodeSalam': Crypter.encrypt(codeSalam),
        'Authorization': authHeader(),
        'Content-Type': 'application/json'
      }
    };

    axios.request(config)
      .then(response => {
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        if (res.StatutCode === 200) {
          const table4 = res.Result.Table4 || [];
          setFraisData(table4);

          const options = table4.map((item: any) => ({
            value: item.CODOPE,
            label: `${item.CODOPE} - ${item.LIBELL || ''}`
          }));
          setFraisOptions(options);

          let savedFrais = form.getFieldValue('CodeFrais') || [];
          if (savedFrais.length === 0 && table4.length > 0) {
            savedFrais = table4.map((item: any) => item.CODOPE);
            form.setFieldValue('CodeFrais', savedFrais);
          }
          setFraisSelectionnes(savedFrais);
        }
      })
      .catch(error => {
        console.error("Erreur chargement paramètres Salam:", error);
        setFraisData([]);
        setFraisOptions([]);
        setFraisSelectionnes([]);
        form.setFieldValue('FraisService', 0);
        form.setFieldValue('CodeFrais', []);
      });
  };

  useEffect(() => {
    loadFraisParams(CodeSalam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CodeSalam]);

  useEffect(() => {
    let total = 0;
    fraisData.forEach((item: any) => {
      if (fraisSelectionnes.includes(item.CODOPE)) {
        total += Number(item.MONFIX || 0) + (Number(item.POURCE || 0) / 100) * MontantDemande;
      }
    });
    form.setFieldValue('FraisService', Math.round(total * 100) / 100);
  }, [MontantDemande, fraisData, fraisSelectionnes, form]);

  // ===== CHARGEMENT DES DEMANDES =====
  const loadFinancement = () => {
    setPageLoad(false);
    setLoading(true);
    setData([]);

    const searchParams: ParamsPicklist = {
      CodeAgence: user.codage,
      Table: 'DEMAND_FINSALAM',
      Colonne1: 'EtapeActuelle',
      Colonne2: '',
      Init: 'VALIDATION',
      Colagence: 'CODAGE',
      Choix: 34,
    };

    const config = {
      method: 'get',
      url: Data.ApiUrl.AnySearch,
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader(),
        CypherSearch: Crypter.encrypt(JSON.stringify(searchParams)),
      },
    };

    axios
      .request(config)
      .then((response) => {
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data)).Value;
          if (res.StatutCode === 200) {
            const datas = res.Result.Table;
            if (datas.length === 0) {
              SweetAlert.fire({
                title: 'Aucune demande Salam en attente de validation',
                text: 'Toutes les demandes ont été traitées.',
                icon: 'info'
              });
            } else {
              const newData = datas.map((item: any, index: number) => ({
                key: index,
                NumeroDemande: item.NumeroDemande,
                CodeSalam: item.CodeSalam,
                NUMCLI: item.NUMCLI,
                NOMCLI: item.NOMCLI,
                MontantDemande: item.MontantDemande,
                DateDemande: item.DateDemande ? new Date(item.DateDemande).toLocaleDateString('fr') : '',
                CODAGE: item.CODAGE,
                CODGEA: item.CODGEA,
              }));
              setData(newData);
            }
          }
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        SweetAlert.fire({
          title: 'Erreur',
          text: error.toString(),
          icon: 'error'
        });
      });
  };

  useEffect(() => {
    if (pageLoad) {
      loadFinancement();
    }
  }, [pageLoad]);

  const columns: ColumnsType<any> = [
    { title: 'Date Demande', dataIndex: 'DateDemande' },
    { title: 'N° Demande', dataIndex: 'NumeroDemande' },
    { title: 'Type Salam', dataIndex: 'CodeSalam' },
    { title: 'Client', dataIndex: 'NOMCLI' },
    { title: 'Montant', dataIndex: 'MontantDemande', render: (v) => formatNumber(v) },
    { title: 'Gestionnaire', dataIndex: 'CODGEA' },
  ];

  // ===== CHARGEMENT DES DÉTAILS D'UNE DEMANDE =====
  const handleDemandeSalam = (numeroDemande: string) => {
    setLoading(true);
    const encryptedNum = Crypter.encrypt(numeroDemande);
    const config = {
      method: 'POST',
      url: Data.ApiUrl.ReadFinancementSalam,
      headers: {
        Authorization: authHeader(),
        'Content-Type': 'application/json',
        CypherNumeroDemandeSalam: encryptedNum,
        CypherAction: Crypter.encrypt('READ_DEMANDE'),
      },
    };
    axios
      .request(config)
      .then((response) => {
        setLoading(false);
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        if (res.StatutCode === 200) {
          const data = res.Result.Table1[0];
          const codeFrais = data.CodeFrais || [];

          const formValues = {
            ...data,
            DateLivraison: data.DateLivraison ? ReformatDate(data.DateLivraison) : '',
            PrixUnitaire: data.Quantite > 0 && data.MontantDemande > 0
              ? Math.round((data.MontantDemande / data.Quantite) * 100) / 100
              : 0,
            CodeFrais: codeFrais,
          };
          form.setFieldsValue(formValues);

          loadFraisParams(data.CodeSalam);
          setFraisSelectionnes(codeFrais);

          // Garanties
          const garanties = res.Result.Table2 || [];
          if (garanties.length > 0) {
            const mapped = garanties.map((g: any) => ({
              codgar: g.CodeGarantie,
              VALGARANTI: g.VALGARANTI || 0,
              valCouvert: g.MontantCouvert || 0,
            }));
            setGarantiesTableau(mapped);
            localStorage.setItem(Data.LocalVariable.garantie, JSON.stringify(mapped));
          } else {
            setGarantiesTableau([]);
          }

          // ============================================================
          // ✅ CORRECTION : synchroniser les documents avec le Form
          // ============================================================
          const docs = res.Result.Table3 || [];
          setDocumentsAttaches(docs);
          form.setFieldValue('Document', docs);   // ← indispensable pour la validation

          setModifActve(false);
          setAnnulActive(false);
        } else {
          SweetAlert.fire({
            title: res.Message || "Demande non trouvée",
            text: res.Result || "Vérifiez le numéro de la demande.",
            icon: "warning"
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        SweetAlert.fire({
          title: 'Erreur',
          text: error.toString(),
          icon: 'error'
        });
      });
  };

  // ===== SÉLECTION DANS LE TABLEAU =====
  const onSelectChange = (newSelectedRowKeys: any) => {
    const index = Array.isArray(newSelectedRowKeys) ? newSelectedRowKeys[0] : newSelectedRowKeys;
    if (index !== undefined && data[index]) {
      const record = data[index];
      if (record.NumeroDemande) {
        form.setFieldsValue(record);
        handleDemandeSalam(record.NumeroDemande);
      } else {
        SweetAlert.fire({
          title: "Numéro de demande manquant",
          text: "Impossible de charger les détails de cette demande.",
          icon: "warning"
        });
      }
    }
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  // ============================================================
  // ✅ CORRECTION : inclure Documents et CodeUti dans le payload
  // ============================================================
  const buildDecisionPayload = (values: any, commentaire: string, montant: number) => ({
    NumeroDemande: values.NumeroDemande,
    UtilVal: user.coduti,
    CodeUti: user.coduti,                 // ← utile pour certaines branches backend
    MontantValide: montant,
    CommentaireValide: commentaire,
    Documents: values.Document || [],     // ← les documents joints (peut être [])
    Garanties: garantiesTableau.map((g: any) => ({
      CodeGarantie: g.codgar,
      MontantCouvert: g.valCouvert ?? 0,
    })),
  });

  // ===== VALIDATION =====
  const handleValidate = () => {
    const values = form.getFieldsValue(true);

    // Garde-fou : empêcher de valider sans avoir sélectionné une demande
    if (!values.NumeroDemande) {
      SweetAlert.fire({
        title: 'Aucune demande sélectionnée',
        text: 'Veuillez sélectionner une demande dans le tableau avant de valider.',
        icon: 'warning',
      });
      return;
    }

    const payload = buildDecisionPayload(
      values,
      values.CommentaireDemande || 'Validation approuvée',
      values.MontantDemande,
    );

    SweetAlert.fire({
      title: `Voulez-vous valider la demande Salam de type ${values.CodeSalam}\n du client : ${values.NUMCLI}\n à hauteur du montant : ${formatNumber(values.MontantDemande)} XAF ?`,
      showCancelButton: true,
      confirmButtonText: "Oui, valider",
      cancelButtonText: "Non",
      icon: "question",
      confirmButtonColor: "green",
      cancelButtonColor: "darkred"
    }).then((result) => {
      if (result.isConfirmed) {
        performDecision(payload, "1");
      }
    });
  };

  // ===== REJET =====
  const handleReject = () => {
    const values = form.getFieldsValue(true);

    if (!values.NumeroDemande) {
      SweetAlert.fire({
        title: 'Aucune demande sélectionnée',
        text: 'Veuillez sélectionner une demande dans le tableau avant de rejeter.',
        icon: 'warning',
      });
      return;
    }

    SweetAlert.fire({
      title: `Rejet de la demande ${values.NumeroDemande}`,
      input: 'textarea',
      inputPlaceholder: 'Motif du rejet (obligatoire)',
      showCancelButton: true,
      confirmButtonText: "Confirmer le rejet",
      cancelButtonText: "Annuler",
      icon: "warning",
      confirmButtonColor: "red",
      cancelButtonColor: "darkgreen",
      inputValidator: (value) => {
        if (!value || !value.trim()) return 'Le motif du rejet est obligatoire.';
        return null;
      },
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        const payload = buildDecisionPayload(values, result.value, 0);
        performDecision(payload, "2");
      }
    });
  };

  // ===== DÉCISION (validation/rejet) =====
  const performDecision = (payload: any, action: string) => {
    setLoading(true);
    const CypherAction = Crypter.encrypt(action);
    const encryptedData = Crypter.encrypt(JSON.stringify(payload));

    const config = {
      method: 'POST',
      url: Data.ApiUrl.ValidationSalam,
      headers: {
        Authorization: authHeader(),
        'Content-Type': 'application/json',
        CypherAction: CypherAction,
      },
      data: encryptedData,
    };

    axios
      .request(config)
      .then((response) => {
        setLoading(false);
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        if (res.StatutCode === 200 || res.StatutCode === 201) {
          SweetAlert.fire({
            title: res.Message,
            text: res.Result,
            icon: 'success'
          });
          handleAnnul();
          loadFinancement();
        } else {
          SweetAlert.fire({
            title: res.Message || 'Erreur',
            text: res.Result || '',
            icon: 'warning'
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error('[ValidationSalam]', error);
        SweetAlert.fire({
          title: 'Erreur',
          text: error.toString(),
          icon: 'error'
        });
      });
  };

  // ===== ANNULATION =====
  const handleAnnul = () => {
    form.resetFields();
    setGarantiesTableau([]);
    setDocumentsAttaches([]);
    setFraisData([]);
    setFraisOptions([]);
    setFraisSelectionnes([]);
    form.setFieldValue('FraisService', 0);
    form.setFieldValue('CodeFrais', []);
    form.setFieldValue('Document', []);
    setModifActve(true);
    setAnnulActive(true);
    setSelectedRowKeys([]);
  };

  // ===== ONGLETS =====
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: <span><UserOutlined /> Détails demande</span>,
      children: <Tab1 form={form} />,
    },
    {
      key: '2',
      label: <span><UserOutlined /> Garanties</span>,
      children: <Tab2 form={form} garantiesTableau={garantiesTableau} />,
    },
    {
      key: '3',
      label: <span><UserOutlined /> Documents</span>,
      children: <Tab3 documents={documentsAttaches} form={form} />,
    },
    {
      key: '4',
      label: <span><UserOutlined /> Frais de Service</span>,
      children: (
        <Tab4
          form={form}
          fraisOptions={fraisOptions}
          fraisSelectionnes={fraisSelectionnes}
          setFraisSelectionnes={setFraisSelectionnes}
          disabled={true}
        />
      ),
    },
  ];

  // ===== RENDU =====
  return (
    <div className="tab">
      <Typography align="center" className="formTitle">
        <b>VALIDATION SALAM</b>
      </Typography>
      <Spin spinning={loading} size="large" style={{ color: 'green' }}>
        <Form form={form} autoComplete="off" labelAlign="left" style={{ minWidth: 'auto', maxWidth: 'auto' }}>
          <div className="bG">
            <legend><Typography color="darkgreen">Actions</Typography></legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={modifActve}
                  onClick={handleValidate}
                  type="ghost"
                  htmlType="button"
                  block
                  style={{ backgroundColor: 'green', color: 'white' }}
                >
                  Valider
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={modifActve}
                  onClick={handleReject}
                  type="ghost"
                  htmlType="button"
                  block
                  style={{ backgroundColor: 'red', color: 'white' }}
                >
                  Rejeter
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={annulActive}
                  onClick={handleAnnul}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className="clientP">
            <Tabs size="small" type="card" defaultActiveKey="1" items={items} />
            <Box className="border-left tabElementAuto" sx={{ mt: 11 }}>
              <Spin spinning={loading}>
                <Form disabled={false}>
                  <Table
                    size="small"
                    style={{ width: 'auto' }}
                    rowSelection={{ ...rowSelection, type: 'radio' }}
                    columns={columns}
                    dataSource={data}
                    scroll={{ y: 200, x: 10 }}
                    pagination={false}
                  />
                </Form>
              </Spin>
            </Box>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default Validation;